import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../dataManagement/uiDb/UiDb";
import { fetchBusinessById, fetchBusinessListings, fetchBusinessRatings } from "../services/thunks/BusinessThunk";
import { clearCurrentBusiness, clearRatingsSummary, setCurrentBusinessProducts, setCurrentBusinessTotalCount, clearCurrentBusinessProducts, clearCurrentBusinessGallery } from "../dataManagement/uiDb/BusinessDb";
import { BusinessProfile } from "../interfaces/BusinessObject";
import { ProductDetailsModal } from "../interfaces/ProductObject";

interface UseBusinessListingsResult {
  business: BusinessProfile | null;
  products: ProductDetailsModal[];
  totalCount: number;
  isLoading: boolean;
  /** Just the business-object fetch — use this (not isLoading) for decisions
   *  that only depend on the business itself (e.g. subscription fallback
   *  state), so an unrelated slow listings fetch can't hold that up. */
  isBusinessLoading: boolean;
  error: string | null;
  loadMore: () => void;
  /** Re-fetches the full listing set. */
  reload: () => Promise<void>;
  /** Syncs optimistic updates (deletes, status changes) back to the Redux cache so they survive back-navigation. */
  setProducts: (updater: ProductDetailsModal[] | ((prev: ProductDetailsModal[]) => ProductDetailsModal[])) => void;
}

export function useBusinessListings(businessId: string): UseBusinessListingsResult {
  const dispatch: AppDispatch = useDispatch();
  // Tracks the last businessId actually fetched (not just "have we ever
  // fetched") — a boolean ref here would fetch only once ever, since React
  // Router reuses this same component instance across /business/:id ->
  // /business/:otherId navigations (same route, only the param changes).
  const fetchedForRef = useRef<string | null>(null);

  const business = useSelector((state: RootState) => state.business.currentBusiness);
  const products = useSelector((state: RootState) => state.business.currentBusinessProducts);
  const totalCount = useSelector((state: RootState) => state.business.currentBusinessTotalCount);
  const loadingMap = useSelector((state: RootState) => state.business.loadingMap);
  const errorMap = useSelector((state: RootState) => state.business.errorMap);
  const ratingsSummary = useSelector((state: RootState) => state.business.ratingsSummary);

  const reload = useCallback(async () => {
    if (!businessId) return;
    const action = await dispatch(fetchBusinessListings({ businessId, ignoreIds: [] }));
    if (fetchBusinessListings.fulfilled.match(action)) {
      const rawPayload = action.payload as any;
      const payload = (rawPayload?.contents?.[0] ?? rawPayload) as { products?: ProductDetailsModal[]; total_count?: number };
      dispatch(setCurrentBusinessProducts(payload.products ?? []));
      dispatch(setCurrentBusinessTotalCount(payload.total_count ?? 0));
    }
  }, [dispatch, businessId]);

  // Lets a caller optimistically patch the loaded product list (deletes,
  // status changes) directly in the Redux cache, so the update survives a
  // back-navigation remount instead of only living in local component state.
  const setProducts = useCallback((updater: ProductDetailsModal[] | ((prev: ProductDetailsModal[]) => ProductDetailsModal[])) => {
    const next = typeof updater === 'function' ? updater(products) : updater;
    dispatch(setCurrentBusinessProducts(next));
  }, [dispatch, products]);

  useEffect(() => {
    if (!businessId || fetchedForRef.current === businessId) return;
    fetchedForRef.current = businessId;

    // If it's a completely different business, clear it out.
    // If it's the SAME business, we can just skip refetching and rely on the Redux cache!
    // This allows back navigation from Product Details to instantly restore the products list and scroll position.
    if (business?.businessId !== businessId) {
      dispatch(clearCurrentBusiness());
      dispatch(clearRatingsSummary());
      dispatch(clearCurrentBusinessProducts());
      dispatch(clearCurrentBusinessGallery());
      dispatch(fetchBusinessById(businessId));
      dispatch(fetchBusinessRatings(businessId));
      reload();
    } else {
      // If it's the SAME business in Redux (e.g. currentBusiness was loaded by DineIn or another screen),
      // but products were never loaded yet, we must fetch listings!
      if (!products || products.length === 0) {
        reload();
      }
      if (!ratingsSummary) {
        dispatch(fetchBusinessRatings(businessId));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, businessId]);

  const loadMore = () => {
    const ignoreIds = products.map((p) => p.productId);
    dispatch(fetchBusinessListings({ businessId, ignoreIds })).then((action) => {
      if (fetchBusinessListings.fulfilled.match(action)) {
        const rawPayload = action.payload as any;
        const payload = (rawPayload?.contents?.[0] ?? rawPayload) as { products?: ProductDetailsModal[]; total_count?: number };
        dispatch(setCurrentBusinessProducts([...products, ...(payload.products ?? [])]));
      }
    });
  };

  const isBusinessLoading = !!loadingMap.fetchBusinessById;
  const isLoading = isBusinessLoading || !!loadingMap.fetchBusinessListings;
  const error = errorMap.fetchBusinessById ?? errorMap.fetchBusinessListings ?? null;

  return {
    business,
    products,
    totalCount,
    isLoading,
    isBusinessLoading,
    error,
    loadMore,
    reload,
    setProducts,
  };
}
